/**
 * Production-safe logger utility
 * Only logs in development environment to prevent sensitive data exposure in production
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

class Logger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV !== 'production';
  }

  private shouldLog(): boolean {
    return this.isDevelopment;
  }

  log(...args: any[]): void {
    if (this.shouldLog()) {
      console.log(...args);
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog()) {
      console.info(...args);
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog()) {
      console.warn(...args);
    }
  }

  error(...args: any[]): void {
    if (this.shouldLog()) {
      console.error(...args);
    }
  }

  debug(...args: any[]): void {
    if (this.shouldLog()) {
      console.debug(...args);
    }
  }

  /**
   * Force log to console even in production (use sparingly for critical errors)
   */
  forceLog(level: LogLevel, ...args: any[]): void {
    console[level](...args);
  }

  /**
   * Log user actions (only in development)
   */
  logUserAction(action: string, context?: any): void {
    if (this.shouldLog()) {
      console.log(`[User Action] ${action}`, context);
    }
  }

  /**
   * Log API errors (only in development)
   */
  logApiError(url: string, method: string, status: number, error?: any): void {
    if (this.shouldLog()) {
      console.error(`[API Error] ${method} ${url} - Status: ${status}`, error);
    }
  }
}

// Export singleton instance
export const logger = new Logger();
export default logger;
